import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Alert, AlertDescription } from '../ui/alert';
import { Languages, Globe, Loader2, Code, AlertCircle, Package } from 'lucide-react';
import { storeService, StoreView } from '../../services/store';

interface SyncDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (storeViewCode: string, inventorySourceCode: string) => void;
  storeName: string;
  storeId: string;
  isLoading?: boolean;
}

export function SyncDialog({
  open,
  onOpenChange,
  onConfirm,
  storeName,
  storeId,
  isLoading = false,
}: SyncDialogProps) {
  const [selectedView, setSelectedView] = useState('default');
  const [customCode, setCustomCode] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [storeViews, setStoreViews] = useState<StoreView[]>([]);
  const [loadingViews, setLoadingViews] = useState(false);
  const [viewsError, setViewsError] = useState<string | null>(null);
  const [isFallback, setIsFallback] = useState(false);
  const [inventorySourceCode, setInventorySourceCode] = useState('online_wh');

  useEffect(() => {
    if (open && storeId) {
      fetchStoreViews();
    }
  }, [open, storeId]);

  const fetchStoreViews = async () => {
    setLoadingViews(true);
    setViewsError(null);
    
    try {
      const response = await storeService.getStoreViews(storeId);
      setStoreViews(response.store_views);
      setIsFallback(response.fallback || false);
      
      if (response.fallback && response.message) {
        setViewsError(response.message);
      }
    } catch (error: any) {
      console.error('Failed to fetch store views:', error);
      setViewsError('Failed to fetch store views. Please enter store view code manually.');
      // Set default store view as fallback
      setStoreViews([{ code: 'default', name: 'Default Store View', id: 0, is_active: true }]);
      setIsFallback(true);
    } finally {
      setLoadingViews(false);
    }
  };

  const handleConfirm = () => {
    onConfirm(useCustom && customCode ? customCode : selectedView, inventorySourceCode);
  };

  const handleRadioChange = (value: string) => {
    setSelectedView(value);
    setUseCustom(value === 'custom');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Languages className="h-5 w-5" />
            Select Store Language
          </DialogTitle>
          <DialogDescription>
            Choose which language version of your products to sync from <strong>{storeName}</strong>.
            <br />
            <span className="text-xs text-muted-foreground mt-1">
              Note: These store view codes must match your Magento configuration. Common codes are: default, en_US, ar_SA, or custom codes like 'english', 'arabic'.
            </span>
          </DialogDescription>
        </DialogHeader>

        {viewsError && (
          <Alert className="mb-4 flex-shrink-0">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{viewsError}</AlertDescription>
          </Alert>
        )}

        <div className="flex-1 overflow-y-auto min-h-0 py-4">
          {loadingViews ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Loading store views...</span>
            </div>
          ) : (
            <>
              {storeViews.length > 3 && (
                <div className="text-xs text-muted-foreground text-center mb-2">
                  {storeViews.length} store views available • Scroll to see all
                </div>
              )}
              <RadioGroup value={selectedView} onValueChange={handleRadioChange}>
                <div className="space-y-3 px-1">
                  {/* Dynamically render fetched store views */}
                  {storeViews.map((view) => (
                  <div key={view.code} className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors">
                    <RadioGroupItem value={view.code} id={view.code} />
                    <Label htmlFor={view.code} className="flex-1 cursor-pointer">
                      <div className="flex items-center gap-2">
                        {view.code === 'default' && <Globe className="h-4 w-4 text-muted-foreground" />}
                        {view.code.startsWith('en') && <span className="text-base">🇬🇧</span>}
                        {view.code.startsWith('ar') && <span className="text-base">🇸🇦</span>}
                        {!view.code.startsWith('en') && !view.code.startsWith('ar') && view.code !== 'default' && 
                          <Globe className="h-4 w-4 text-muted-foreground" />}
                        <span className="font-medium">{view.name}</span>
                        <span className="text-xs text-muted-foreground">({view.code})</span>
                      </div>
                      {!view.is_active && (
                        <p className="text-xs text-destructive mt-1">This store view is inactive</p>
                      )}
                    </Label>
                  </div>
                ))}

                  {/* Always show custom option */}
                <div className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors">
                  <RadioGroupItem value="custom" id="custom" />
                  <Label htmlFor="custom" className="flex-1 cursor-pointer">
                    <div className="flex items-center gap-2">
                      <Code className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">Custom Store View Code</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      Enter your Magento store view code manually
                    </p>
                  </Label>
                </div>

                {useCustom && (
                  <div className="ml-7 pl-3">
                    <Input
                      type="text"
                      placeholder="e.g., english, arabic, store_en, etc."
                      value={customCode}
                      onChange={(e) => setCustomCode(e.target.value)}
                      className="w-full"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Enter the exact store view code from your Magento configuration
                    </p>
                  </div>
                )}
                </div>
              </RadioGroup>
            </>
          )}
        </div>

        {/* Inventory Source Code Section */}
        <div className="flex-shrink-0 border-t pt-4 mt-4">
          <Label htmlFor="inventory-source" className="text-sm font-medium flex items-center gap-2">
            <Package className="h-4 w-4" />
            Inventory Source Code
          </Label>
          <p className="text-xs text-muted-foreground mt-1 mb-2">
            Specify the inventory source to fetch stock data from (e.g., 'online_wh' for Online Warehouse).
          </p>
          <Input
            id="inventory-source"
            type="text"
            placeholder="e.g., online_wh, default, warehouse1, etc."
            value={inventorySourceCode}
            onChange={(e) => setInventorySourceCode(e.target.value)}
            className="w-full"
          />
        </div>

        <DialogFooter className="flex-shrink-0 border-t pt-4 mt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Starting Sync...
              </>
            ) : (
              <>
                <Languages className="mr-2 h-4 w-4" />
                Start Sync
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}